import { useRouter } from 'next/router';
import * as React from 'react';

import aiAskSourceStyles from '../../AIAskSources.module.css';
import type { AiAskIndexData } from '../../utils/aiAskHelpers';
import {
  isVersionFilePreviewSource,
  refToPseudoSource,
  sourceDisplayTitle,
  sourceHref,
  sourceIndexTypeDisplayLabel,
  sourceObjectId,
} from '../../utils/aiAskHelpers';
import { openVersionFilePreview } from '../../utils/openVersionFilePreview';

type SourceCardRow = Record<string, unknown> & { displayOrder: number };

export const SourceCardsBlock: React.FC<{
  sources: unknown[];
  references: unknown[];
  appOrigin: string;
  indexData?: AiAskIndexData;
}> = ({ sources, references, appOrigin, indexData }) => {
  const router = useRouter();
  let sourceRows: SourceCardRow[] = [];

  if (Array.isArray(sources) && sources.length) {
    sourceRows = sources.map((source, sourceIndex) => ({
      ...(typeof source === 'object' && source
        ? (source as Record<string, unknown>)
        : {}),
      displayOrder: sourceIndex + 1,
    }));
  } else if (Array.isArray(references) && references.length) {
    sourceRows = references
      .map((reference, referenceIndex) => {
        const pseudoSource = refToPseudoSource(reference);
        return pseudoSource
          ? { ...pseudoSource, displayOrder: referenceIndex + 1 }
          : null;
      })
      .filter(Boolean) as SourceCardRow[];
  }

  if (!sourceRows.length) {
    return null;
  }

  const sourceStyles = aiAskSourceStyles;

  return (
    <div className={sourceStyles.sourceCards}>
      <div className={sourceStyles.sourceCardsTitle}>Sources</div>
      <ul className={sourceStyles.sourceCardsList}>
        {sourceRows.map((sourceRow, rowIndex) => {
          const sourceLink = sourceHref(sourceRow, appOrigin);
          const sourceTitle = sourceDisplayTitle(
            sourceRow,
            sourceRow.displayOrder ?? rowIndex + 1,
            indexData
          );
          const indexTypeLabel = sourceIndexTypeDisplayLabel(sourceRow);
          const objectId = sourceObjectId(sourceRow);
          const opensFilePreview = isVersionFilePreviewSource(sourceRow);

          return (
            <li
              key={`${objectId || rowIndex}-${rowIndex}`}
              className={sourceStyles.sourceCard}
            >
              <span className={sourceStyles.sourceCardBadge}>
                {sourceRow.displayOrder ?? rowIndex + 1}
              </span>
              <div className={sourceStyles.sourceCardBody}>
                {sourceLink ? (
                  <a
                    className={`${sourceStyles.sourceCardTitle} ${sourceStyles.sourceCardTitleLink}`}
                    href={sourceLink}
                    onClick={
                      opensFilePreview
                        ? (event) => {
                            event.preventDefault();
                            openVersionFilePreview(router, objectId);
                          }
                        : undefined
                    }
                  >
                    {sourceTitle}
                    <span className={sourceStyles.sourceCardExt} aria-hidden="true">
                      {' '}
                      ↗
                    </span>
                  </a>
                ) : (
                  <div
                    className={`${sourceStyles.sourceCardTitle} ${sourceStyles.sourceCardTitlePlain}`}
                  >
                    {sourceTitle}
                  </div>
                )}
                {indexTypeLabel ? (
                  <div className={sourceStyles.sourceCardMeta}>
                    {indexTypeLabel}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
